import React, { useCallback, useEffect, useRef, useState } from 'react';
import { locationIsKia } from '../../../utils/helpers';
import { AppProps } from '../../App';
import { PdfContentMenuDropdown } from '../../components/pdf/PdfContentMenuDropdown';
import { PdfDropdown } from '../../components/pdf/PdfDropdown';
import { PdfHeading } from '../../components/pdf/PdfHeading';
import { PdfImage } from '../../components/pdf/PdfImage';
import { PdfLead } from '../../components/pdf/PdfLead';
import { PdfOrderedList } from '../../components/pdf/PdfOrderedList';
import { PdfParagraph } from '../../components/pdf/PdfParagraph';
import { PdfSelector } from '../../components/pdf/PdfSelector';
import { PdfSubHeading } from '../../components/pdf/PdfSubHeading';
import { PdfTip } from '../../components/pdf/PdfTip';
import { PdfUnorderedList } from '../../components/pdf/PdfUnorderedList';
import { Lead, PdfItem } from './types';

const Pdf: React.FC = ({ pdfName }: Pick<AppProps, 'pdfName'>) => {
  const [pdf, setPdf] = useState<PdfItem[] | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0);
  const [menuTitle, setMenuTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [scrolledPast, setScrolledPast] = useState(false);

  const loadPdfData = async (slug: string) => {
    const modulesMap: { [key: string]: any } = {
      'matt-lack': () => import('./matt-lack-copy'),
    };

    try {
      if (modulesMap[slug]) {
        const pdfData = await modulesMap[slug]();
        setPdf(pdfData.pdf);
        setLead(pdfData.lead);
        setSubject(pdfData.tableOfContents.subject);
        setMenuTitle(pdfData.tableOfContents.menuTitle);
        setKey((prevKey) => prevKey + 1);
      } else {
        console.error('Module not found for slug:', slug);
      }
    } catch (error) {
      console.error('Error loading pdf data', error);
    }
  };
  const handlePdfSelect = useCallback((slug: string) => {
    loadPdfData(slug);
  }, []);

  useEffect(() => {
    if (pdfName) {
      loadPdfData(pdfName);
    }
  }, [pdfName]);

  useEffect(() => {
    const handleScroll = () => {
      if (!pdfRef.current) return;
      const { top } = pdfRef.current.getBoundingClientRect();
      setScrolledPast(top < 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <div>
      {!locationIsKia() ? (
        <>
          <PdfSelector onPdfSelect={handlePdfSelect} />
          <div className="snb-parent sticky top-0 w-full h-20 sm:h-32 bg-blue-500">
            Mocked kia sticky header
          </div>
        </>
      ) : null}
      <div ref={pdfRef} className="w-full max-w-screen-kia-sm mx-auto ">
        {pdf && (
          <PdfDropdown
            scrolledPast={scrolledPast}
            menuTitle={menuTitle}
            subject={subject}
          >
            {(closeDropdown) => (
              <PdfContentMenuDropdown
                key={key}
                containerRef={containerRef}
                closeDropdown={closeDropdown}
              />
            )}
          </PdfDropdown>
        )}

        <div className="flex flex-col justify-center">
          {lead && (
            <PdfLead
              key="lead"
              data={{
                leadTitle: lead.leadTitle,
                content: lead.content,
              }}
            />
          )}
          <div
            ref={containerRef}
            className="flex flex-col gap-5 mt-8 mb-4 w-full"
          >
            {pdf
              ? pdf.map((item, index) => {
                  switch (item.type) {
                    case 'heading':
                      return <PdfHeading key={index} data={item.data} />;
                    case 'sub-heading':
                      return <PdfSubHeading key={index} data={item.data} />;
                    case 'paragraph':
                      return <PdfParagraph key={index} data={item.data} />;
                    case 'tip':
                      return <PdfTip key={index} data={item.data} />;
                    case 'ul':
                      return <PdfUnorderedList key={index} data={item.data} />;
                    case 'ol':
                      return <PdfOrderedList key={index} data={item.data} />;
                    case 'img':
                      return <PdfImage key={index} data={item.data} />;
                    default:
                      return null;
                  }
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pdf;
